import { getExportAndAccessRequestDetailsPath } from 'modules/requests/requestDetailPaths'
import type { FileChangeStatus, ReRequest, Result } from 'services/results/types'
import { formatDate } from 'utils/date'

import { isDifferentRequester as getIsDifferentRequester } from '../utils'

const parseOriginalApprovalMethod = (
  originalApprovalMethod?: ReRequest['originalApprovalMethod'],
  approvedByEmail?: string
): string => {
  if (!originalApprovalMethod) return '-'

  switch (originalApprovalMethod) {
    case 'automated':
      return 'Automated checks pipeline — checks passed'
    case 'manualApproval':
      return approvedByEmail ? `Manually approved by ${approvedByEmail}` : 'Manually approved'
    case 'manualApprovalAfterAutoCheckFailed':
      return approvedByEmail
        ? `Manually approved by ${approvedByEmail} after automated checks failed`
        : 'Manually approved after automated checks failed'
    default:
      return '-'
  }
}

interface VerificationField {
  label: string
  value: string
}

const getVerificationField = (
  fileChangeStatus?: FileChangeStatus,
  isDifferentRequester?: boolean
): VerificationField => {
  if (!fileChangeStatus) return { label: 'ETag status', value: '-' }

  if (fileChangeStatus === 'unchanged' && !isDifferentRequester) {
    return { label: 'Verification method', value: 'File was not changed.' }
  }

  if (fileChangeStatus === 'changed') {
    return { label: 'ETag status', value: 'Mismatch — the file content has changed since original approval' }
  }

  return { label: 'ETag status', value: 'Match — the file content has not changed since original approval' }
}

const getCardTitle = (fileChangeStatus?: FileChangeStatus): string => {
  if (fileChangeStatus === 'changed') {
    return 'Previously approved request — but the file has since changed'
  }
  return 'Previously approved request'
}

interface UsePreviousApprovalDetailsProps {
  result: Result
}

const usePreviousApprovalDetails = ({ result }: UsePreviousApprovalDetailsProps) => {
  const firstOriginalRequestIdentifierUrl = getExportAndAccessRequestDetailsPath(result.reRequest?.rootRequestId ?? '')
  const originallyApprovedBy = result.reRequest?.originallyApprovedBy
  const originalApprovalDate = formatDate(result.reRequest?.originalApprovalDate)
  const originalApprovalMethod = parseOriginalApprovalMethod(
    result.reRequest?.originalApprovalMethod,
    result.reRequest?.originallyApprovedBy
  )

  const isDifferentRequester = getIsDifferentRequester(result)

  const verificationField = getVerificationField(result.fileChangeStatus, isDifferentRequester)
  const cardTitle = getCardTitle(result.fileChangeStatus)

  return {
    cardTitle,
    firstOriginalRequestIdentifierUrl,
    originallyApprovedBy,
    originalApprovalDate,
    originalApprovalMethod,
    verificationLabel: verificationField.label,
    verificationValue: verificationField.value
  }
}

export default usePreviousApprovalDetails
